import {
  Shield,
  Cloud,
  Code,
  BrainCircuit,
  Scale,
  Building2,
  Lightbulb,
  Blocks,
  Siren,
  Users,
  FileText,
  ArrowLeftRight,
  Flag,
  ScanSearch,
  Layers,
  Banknote,
} from "lucide-react";

// Matches backend Veritas.Domain.Entities.AgentType
export enum AgentType {
  Cybersecurity = 0,
  InfrastructureCloud = 1,
  SoftwareEngineering = 2,
  DataAI = 3,
  Compliance = 4,
  VendorThirdParty = 5,
  IpLicensing = 6,
  ArchitectureScalability = 7,
  IncidentOps = 8,
  PeopleOrg = 9,
  Summarization = 10,
  ContradictionDetector = 11,
  RedFlagClassifier = 12,
  GapDetector = 13,
  CrossDocSynthesis = 14,
  FinancialDueDiligence = 15,
}

// Matches backend Veritas.Domain.Entities.WorkstreamStatus
export enum WorkstreamStatus {
  Pending = 0,
  Running = 1,
  Completed = 2,
  Failed = 3,
}

// Matches backend Veritas.Application.Features.Workstreams.WorkstreamDto
export interface WorkstreamDto {
  id: number;
  projectId: number;
  agentType: AgentType;
  status: WorkstreamStatus;
  summary: string | null;
  findingsJson: string | null;
  riskScore: number | null;
  keyGaps: string | null;
  redFlags: string | null;
  keyGapsCount: number | null;
  redFlagsCount: number | null;
  documentType: string | null;
  confidence: number | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

// Parsed from WorkstreamDto.findingsJson
export interface WorkstreamFinding {
  title: string;
  severity: string;
  description: string;
  evidence: string;
  recommendation: string;
}

// All agent types displayed in the UI
export const DOMAIN_AGENT_TYPES: AgentType[] = [
  AgentType.Cybersecurity,
  AgentType.InfrastructureCloud,
  AgentType.SoftwareEngineering,
  AgentType.DataAI,
  AgentType.Compliance,
  AgentType.VendorThirdParty,
  AgentType.IpLicensing,
  AgentType.ArchitectureScalability,
  AgentType.IncidentOps,
  AgentType.PeopleOrg,
  AgentType.FinancialDueDiligence,
  AgentType.Summarization,
  AgentType.ContradictionDetector,
  AgentType.RedFlagClassifier,
  AgentType.GapDetector,
  AgentType.CrossDocSynthesis,
];

// English fallback labels for search matching
export const AGENT_TYPE_LABELS: Record<number, string> = {
  [AgentType.Cybersecurity]: "Cybersecurity",
  [AgentType.InfrastructureCloud]: "Infrastructure & Cloud",
  [AgentType.SoftwareEngineering]: "Software Engineering",
  [AgentType.DataAI]: "Data & AI",
  [AgentType.Compliance]: "Compliance",
  [AgentType.VendorThirdParty]: "Vendor & Third Party",
  [AgentType.IpLicensing]: "IP & Licensing",
  [AgentType.ArchitectureScalability]: "Architecture & Scalability",
  [AgentType.IncidentOps]: "Incident & Operations",
  [AgentType.PeopleOrg]: "People & Organization",
  [AgentType.FinancialDueDiligence]: "Financial Due Diligence",
  [AgentType.Summarization]: "Summarization",
  [AgentType.ContradictionDetector]: "Contradiction Detector",
  [AgentType.RedFlagClassifier]: "Red Flag Classifier",
  [AgentType.GapDetector]: "Gap Detector",
  [AgentType.CrossDocSynthesis]: "Cross-Document Synthesis",
};

// Maps AgentType to its i18n translation key
export const AGENT_TYPE_TRANSLATION_KEYS: Record<number, string> = {
  [AgentType.Cybersecurity]: "agentCybersecurity",
  [AgentType.InfrastructureCloud]: "agentInfrastructureCloud",
  [AgentType.SoftwareEngineering]: "agentSoftwareEngineering",
  [AgentType.DataAI]: "agentDataAI",
  [AgentType.Compliance]: "agentCompliance",
  [AgentType.VendorThirdParty]: "agentVendorThirdParty",
  [AgentType.IpLicensing]: "agentIpLicensing",
  [AgentType.ArchitectureScalability]: "agentArchitectureScalability",
  [AgentType.IncidentOps]: "agentIncidentOps",
  [AgentType.PeopleOrg]: "agentPeopleOrg",
  [AgentType.FinancialDueDiligence]: "agentFinancialDueDiligence",
  [AgentType.Summarization]: "agentSummarization",
  [AgentType.ContradictionDetector]: "agentContradictionDetector",
  [AgentType.RedFlagClassifier]: "agentRedFlagClassifier",
  [AgentType.GapDetector]: "agentGapDetector",
  [AgentType.CrossDocSynthesis]: "agentCrossDocSynthesis",
};

// Maps AgentType to a Lucide icon component
export const AGENT_TYPE_ICONS: Record<number, typeof Shield> = {
  [AgentType.Cybersecurity]: Shield,
  [AgentType.InfrastructureCloud]: Cloud,
  [AgentType.SoftwareEngineering]: Code,
  [AgentType.DataAI]: BrainCircuit,
  [AgentType.Compliance]: Scale,
  [AgentType.VendorThirdParty]: Building2,
  [AgentType.IpLicensing]: Lightbulb,
  [AgentType.ArchitectureScalability]: Blocks,
  [AgentType.IncidentOps]: Siren,
  [AgentType.PeopleOrg]: Users,
  [AgentType.FinancialDueDiligence]: Banknote,
  [AgentType.Summarization]: FileText,
  [AgentType.ContradictionDetector]: ArrowLeftRight,
  [AgentType.RedFlagClassifier]: Flag,
  [AgentType.GapDetector]: ScanSearch,
  [AgentType.CrossDocSynthesis]: Layers,
};

// Safe JSON array parser for findingsJson, keyGaps, redFlags
export function safeParseJsonArray<T>(json: string | null): T[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
